import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { formatTimeAMPM } from "../lib/utils";
import { getOrgTimezone, formatDateTime, getTemporalStatus as getTemporalStatusTZ } from "../lib/timezone";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ClickableAvatar } from "../components/ClickableAvatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  ChevronLeft, ChevronRight, ChevronDown,
  Phone, MessageSquare, CheckCircle2, Clock, AlertCircle,
  X, Plus, Edit2, ExternalLink, List, Search, Eye,
  SlidersHorizontal, Trash2, Pencil, Filter, History,
} from "lucide-react";
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  addMonths, subMonths, isToday,
} from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

const PRIORITY_COLORS = {
  High: "bg-red-500/15 text-red-500 border-red-500/20",
  Medium: "bg-amber-500/15 text-amber-500 border-amber-500/20",
  Low: "bg-blue-500/15 text-blue-500 border-blue-500/20",
};

const STATUS_COLORS = {
  Open: "bg-blue-500/15 text-blue-500 border-blue-500/20",
  Responded: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
  "Not Interested": "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  "Not Responded": "bg-orange-500/15 text-orange-500 border-orange-500/20",
  Closed: "bg-zinc-700/15 text-zinc-500 border-zinc-700/20",
  "Need Followup": "bg-purple-500/15 text-purple-500 border-purple-500/20",
};

const TEMPORAL_STATUS_COLORS = {
  Upcoming: "bg-cyan-500/15 text-cyan-500 border-cyan-500/20",
  Overdue: "bg-red-500/15 text-red-500 border-red-500/20",
  Closed: "bg-zinc-500/15 text-zinc-500 border-zinc-500/20",
};

function getTemporalStatus(f, timezone) {
  return getTemporalStatusTZ(f, timezone);
}

const STATUS_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "Closed", label: "Closed" },
  { value: "Need Followup", label: "Need Followup" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const EMPTY_FILTERS = {
  dateFrom: "", dateTo: "", typeId: "",
  priority: "", assignedStaffId: "", status: "",
};

// ── Helpers ────────────────────────────────────────────────────────────────────────────────────
function buildFilterParams(filters, currentBranch) {
  const params = new URLSearchParams();
  if (currentBranch?.id) params.append("branchId", currentBranch.id);
  if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.append("dateTo", filters.dateTo);
  if (filters.typeId) params.append("typeId", filters.typeId);
  if (filters.priority) params.append("priority", filters.priority);
  if (filters.assignedStaffId) params.append("assignedStaffId", filters.assignedStaffId);
  if (filters.status) params.append("status", filters.status);
  return params;
}

function activeFilterCount(filters) {
  return Object.values(filters).filter(v =>
    Array.isArray(v) ? v.length > 0 : !!v
  ).length;
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

// ── Filter Drawer ────────────────────────────────────────────────────────────────────────
function FiltersDrawer({ open, onOpenChange, filters, onChange, onClear, followupTypes, staffList }) {
  const isDesktop = useIsDesktop();
  const [draft, setDraft] = useState(filters);
  useEffect(() => { if (open) setDraft(filters); }, [open, filters]);

  const handleApply = () => { onChange(draft); onOpenChange(false); };
  const handleClear = () => { setDraft(EMPTY_FILTERS); onClear(); onOpenChange(false); };
  const set = (key, val) => setDraft(prev => ({ ...prev, [key]: val }));

  const draftCount = Object.values(draft).filter(v => v !== "").length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? "right" : "bottom"}
        className={isDesktop
          ? "w-[400px] flex flex-col p-0 bg-card border-border"
          : "rounded-t-2xl flex flex-col p-0 max-h-[92vh] bg-card border-border"
        }
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filters
              {draftCount > 0 && (
                <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">{draftCount} active</Badge>
              )}
            </SheetTitle>
            {draftCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClear} className="text-muted-foreground text-xs h-7">
                <X className="h-3.5 w-3.5 mr-1" />Clear all
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Date Range */}
          <div className="space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Date Range</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" value={draft.dateFrom} onChange={e => set("dateFrom", e.target.value)} data-testid="filter-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" value={draft.dateTo} onChange={e => set("dateTo", e.target.value)} data-testid="filter-date-to" />
              </div>
            </div>
          </div>

          <Separator />

          {/* Type + Staff 2-col */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Type</Label>
              <Select value={draft.typeId || "all"} onValueChange={v => set("typeId", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-type"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {followupTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Staff</Label>
              <Select value={draft.assignedStaffId || "all"} onValueChange={v => set("assignedStaffId", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-staff"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Priority toggle chips */}
          <div className="space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Priority</p>
            <div className="flex flex-wrap gap-2">
              {["High", "Medium", "Low"].map(p => (
                <button key={p} type="button"
                  onClick={() => set("priority", draft.priority === p ? "" : p)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-mono transition-all ${draft.priority === p
                    ? PRIORITY_COLORS[p] + " border-current"
                    : "border-border text-muted-foreground hover:bg-secondary/50"
                    }`} data-testid={`filter-priority-${p.toLowerCase()}`}>{p}
                </button>
              ))}
            </div>
          </div>

          {/* Status toggle chips */}
          <div className="space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(s => (
                <button key={s.value} type="button"
                  onClick={() => set("status", draft.status === s.value ? "" : s.value)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-mono transition-all ${draft.status === s.value
                    ? STATUS_COLORS[s.value] + " border-current"
                    : "border-border text-muted-foreground hover:bg-secondary/50"
                    }`} data-testid={`filter-status-${s.value.toLowerCase().replace(/ /g, "-")}`}>{s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-card shrink-0">
          <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={handleApply}>
            <Search className="h-4 w-4 mr-2" />Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Edit Followup Drawer ───────────────────────────────────────────────────────────────────────────
function EditFollowupDrawer({ open, onOpenChange, followup, followupTypes, staffList, onSuccess }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && followup) {
      setForm({
        title: followup.title || "",
        description: followup.description || "",
        scheduledDate: followup.scheduledDate || "",
        scheduledTime: followup.scheduledTime || "",
        priority: followup.priority || "Medium",
        status: followup.status || "Open",
        assignedStaffId: followup.assignedStaffId || "",
        followupTypeIds: followup.followupTypeIds || [],
      });
    }
  }, [open, followup]);

  if (!followup) return null;

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const toggleType = (id) => {
    setForm(prev => ({
      ...prev,
      followupTypeIds: prev.followupTypeIds.includes(id)
        ? prev.followupTypeIds.filter(t => t !== id)
        : [...prev.followupTypeIds, id],
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.scheduledDate) { toast.error("Scheduled date is required"); return; }
    setLoading(true);
    try {
      await api.put(`/followups/${followup.id}`, {
        title: form.title.trim(),
        description: form.description.trim(),
        scheduledDate: form.scheduledDate,
        scheduledTime: form.scheduledTime || null,
        priority: form.priority,
        status: form.status,
        assignedStaffId: form.assignedStaffId || null,
        followupTypeIds: form.followupTypeIds,
      });
      toast.success("Follow-up updated");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update follow-up");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[460px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <Pencil className="h-4 w-4" />Edit Follow-up
          </SheetTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {followup.memberName} &mdash; <span className="font-mono">{followup.scheduledDate}</span>
          </p>
        </SheetHeader>
        <ScrollArea className="flex-1">
          <div className="px-6 py-5 space-y-5">
            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest">Title *</Label>
              <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Follow-up title" data-testid="edit-title" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest">Description</Label>
              <Textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Optional notes..." rows={3} data-testid="edit-description" />
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest">Date *</Label>
                <Input type="date" value={form.scheduledDate} onChange={e => set("scheduledDate", e.target.value)} data-testid="edit-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest">Time</Label>
                <Input type="time" value={form.scheduledTime} onChange={e => set("scheduledTime", e.target.value)} data-testid="edit-time" />
              </div>
            </div>
            <Separator />
            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest">Priority</Label>
              <div className="flex gap-2">
                {["High", "Medium", "Low"].map(p => (
                  <button key={p} type="button" onClick={() => set("priority", p)}
                    className={`flex-1 py-2 rounded-lg border text-xs font-mono transition-all ${form.priority === p ? PRIORITY_COLORS[p] + " border-current" : "border-border text-muted-foreground hover:bg-secondary/50"
                      }`} data-testid={`edit-priority-${p.toLowerCase()}`}>{p}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest">Status</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Separator />
            {followupTypes.length > 0 && (
              <div className="space-y-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest">Follow-up Types</Label>
                <div className="flex flex-wrap gap-2">
                  {followupTypes.map(t => (
                    <button key={t.id} type="button" onClick={() => toggleType(t.id)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-mono transition-all ${form.followupTypeIds?.includes(t.id)
                        ? "bg-primary/15 text-primary border-primary/30"
                        : "border-border text-muted-foreground hover:bg-secondary/50"
                        }`}>{t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest">Assigned Staff</Label>
              <Select value={form.assignedStaffId || "none"} onValueChange={v => set("assignedStaffId", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="edit-staff"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </ScrollArea>
        <div className="px-6 py-4 border-t border-border flex gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="flex-1 text-xs">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={loading} className="flex-1 text-xs font-heading uppercase tracking-wide" data-testid="edit-save-btn">
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Delete Confirm Dialog ──────────────────────────────────────────────────────────────────────────────
function DeleteFollowupDialog({ open, onOpenChange, followup, onSuccess }) {
  const [loading, setLoading] = useState(false);
  if (!followup) return null;

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/followups/${followup.id}`);
      toast.success("Follow-up deleted");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete follow-up");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />Delete Follow-up
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            This action cannot be undone. All activity history will also be removed.
          </DialogDescription>
        </DialogHeader>
        <div className="my-2 p-4 bg-secondary/30 border border-border rounded-xl space-y-3">
          <div className="flex items-center gap-3">
            <ClickableAvatar
              src={followup.memberPhoto}
              name={followup.memberName}
              className="h-10 w-10"
              fallbackClassName="bg-primary/10 text-primary text-sm"
            />
            <div>
              <p className="font-medium text-sm">{followup.memberName}</p>
              {followup.memberPhone && (
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{followup.memberPhone}</p>
              )}
            </div>
          </div>
          <div>
            <p className="font-medium text-sm">{followup.title}</p>
            {followup.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{followup.description}</p>}
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
            <span className="flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" />{followup.scheduledDate}
              {followup.scheduledTime && ` at ${formatTimeAMPM(followup.scheduledTime)}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${PRIORITY_COLORS[followup.priority] || PRIORITY_COLORS.Medium} border text-[10px] font-mono uppercase`}>{followup.priority}</Badge>
            <Badge className={`${STATUS_COLORS[followup.status] || STATUS_COLORS.Open} border text-[10px] font-mono uppercase`}>{followup.status}</Badge>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} disabled={loading} data-testid="confirm-delete-btn">
            <Trash2 className="h-4 w-4 mr-2" />{loading ? "Deleting..." : "Delete Follow-up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Inline action buttons ───────────────────────────────────────────────────────────────────────────────────────
function FollowupActionButtons({ followup, onView, onEdit, onDelete }) {
  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              onClick={() => onView(followup)} data-testid={`action-view-${followup.id}`}>
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">View Details</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-blue-500 hover:bg-blue-500/10"
              onClick={() => onEdit(followup)} data-testid={`action-edit-${followup.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">Edit Follow-up</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={() => onDelete(followup)} data-testid={`action-delete-${followup.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">Delete Follow-up</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

// ── Mobile Follow-up Card ──────────────────────────────────────────────────────────────────────────────────────
function FollowupMobileCard({ f, expanded, onToggle, onView, onEdit, onDelete, tz }) {
  return (
    <Collapsible open={expanded} onOpenChange={onToggle} asChild>
      <div className=" border border-t-0 hover:bg-secondary/5 transition-colors">
        <CollapsibleTrigger asChild>
          <div className="cursor-pointer select-none">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0 p-2">
                <ClickableAvatar
                  src={f.memberPhoto}
                  name={f.memberName}
                  className="h-9 w-9 shrink-0"
                  fallbackClassName="bg-primary/10 text-primary text-xs font-bold"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate leading-tight">{f.memberName}</p>
                  {f.memberPhone && (
                    <p className="text-[11px] text-muted-foreground font-mono flex items-center gap-1 mt-0.5">
                      <Phone className="h-2.5 w-2.5" />{f.memberPhone}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 pr-1">
                <div className="flex flex-col items-end gap-1">
                  <span className="font-mono text-[10px] tracking-tighter flex items-center gap-1"><CalendarIcon className="h-3 w-3 text-muted-foreground" />{f.scheduledDate}</span>
                  {f.scheduledTime && <span className="font-mono text-[10px] tracking-tighter text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{formatTimeAMPM(f.scheduledTime)}</span>}
                </div>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
              </div>
            </div>
            <div className="flex items-center gap-2 pl-3 mb-1">
              {f.status !== "Closed" && (
                <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(f, tz)]} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>{getTemporalStatus(f, tz)}</Badge>
              )}
              <Badge className={`${STATUS_COLORS[f.status] || STATUS_COLORS.Open} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>{f.status}</Badge>
              <Badge className={`${PRIORITY_COLORS[f.priority] || PRIORITY_COLORS.Medium} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>{f.priority}</Badge>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-3 border-t border-border/40">

            <div className="flex items-center gap-2 p-2 border-b border-border/30">
              <Button variant="outline" size="sm" className="flex-1 h-8 text-xs gap-1" onClick={(e) => { e.stopPropagation(); onView(f); }}>
                <History className="h-3 w-3" /> Details & Notes
              </Button>
              <Button variant="outline" size="sm" className="flex-1 h-8 text-xs gap-1" onClick={(e) => { e.stopPropagation(); onEdit(f); }}>
                <Edit2 className="h-3 w-3" /> Edit
              </Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive hover:bg-destructive/10" onClick={(e) => { e.stopPropagation(); onDelete(f.id); }}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            <div className="p-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Title & Description</p>
              <p className="text-sm font-semibold mt-1">{f.title}</p>
              <p className="text-xs text-muted-foreground leading-normal mt-0.5 whitespace-pre-wrap">{f.description || "No description provided."}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs font-mono text-muted-foreground p-2">
              <div>
                <p className="text-[10px] font-sans font-semibold text-foreground mb-1 uppercase tracking-wider">Scheduled</p>
                <span className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" />{f.scheduledDate}</span>
                {f.scheduledTime && <span className="flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" />{formatTimeAMPM(f.scheduledTime)}</span>}
              </div>
              <div>
                <p className="text-[10px] font-sans font-semibold text-foreground mb-1 uppercase tracking-wider">Assigned Staff</p>
                <span className="text-foreground font-sans font-medium">{f.assignedStaffName || "Not assigned"}</span>
              </div>
            </div>

            <div className="space-y-1 p-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Follow-up Types</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {f.followupTypeNames?.length > 0 ? (
                  f.followupTypeNames.map((name, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] font-mono">{name}</Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </div>
            </div>

          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

const parseMonthParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 2) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
      return new Date(year, month - 1, 1);
    }
  }
  return null;
};

const parseDateParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return new Date(year, month - 1, day);
    }
  }
  return null;
};

import { WhatsApp } from "../components/ui/whatsapp";

const getActionLabel = (action) => {
  switch (action) {
    case "created": return "Created Follow-up";
    case "updated": return "Updated Details";
    case "status_changed": return "Updated Status";
    case "note_added": return "Added Note";
    default: return action ? action.replace("_", " ") : "";
  }
};

const renderNoteOrActivityDetails = (item) => {
  const details = item.details || "";
  const action = item.action || (details.includes("Status changed:") ? "status_changed" : "note_added");

  if (action === "note_added") {
    return (
      <div className="mt-1.5 p-2 bg-amber-500/5 dark:bg-amber-500/10 border-l-4 border-amber-500 rounded-r-lg">
        <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 mb-0.5 uppercase tracking-wide">Response Note</p>
        <p className="text-xs text-foreground whitespace-pre-wrap font-sans leading-relaxed">{details}</p>
      </div>
    );
  }

  if (action === "status_changed") {
    const lines = details.split("\n");
    let statusLine = "";
    let notesLine = "";
    let rescheduleLine = "";

    lines.forEach(line => {
      if (line.startsWith("Status changed:")) {
        statusLine = line.replace("Status changed:", "").trim();
      } else if (line.startsWith("Notes:")) {
        notesLine = line.replace("Notes:", "").trim();
      } else if (line.startsWith("Rescheduled to:")) {
        rescheduleLine = line.replace("Rescheduled to:", "").trim();
      } else {
        if (notesLine) {
          notesLine += "\n" + line;
        } else {
          statusLine += "\n" + line;
        }
      }
    });

    return (
      <div className="mt-1 space-y-1">
        {statusLine && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="text-[10px] uppercase tracking-wide font-medium">Status:</span>
            <span className="font-semibold text-foreground">{statusLine}</span>
          </div>
        )}
        
        {rescheduleLine && (
          <div className="flex items-center gap-2 py-0.5 px-2 bg-purple-500/5 dark:bg-purple-500/10 border border-purple-500/20 rounded text-purple-600 dark:text-purple-400 text-[10px] font-medium w-fit mt-1">
            <CalendarIcon className="h-3 w-3" />
            <span>Rescheduled to: {rescheduleLine}</span>
          </div>
        )}

        {notesLine && (
          <div className="mt-1 p-2 bg-amber-500/5 dark:bg-amber-500/10 border-l-4 border-amber-500 rounded-r-lg">
            <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 mb-0.5 uppercase tracking-wide">Response Note</p>
            <p className="text-xs text-foreground whitespace-pre-wrap font-sans leading-relaxed">{notesLine}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <p className="text-xs text-foreground mt-0.5 whitespace-pre-wrap leading-normal">{details}</p>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────────────────────────────
export default function Followups() {
  const { currentBranch, organization, loading } = useAuth();
  const orgTimezone = getOrgTimezone(organization);

  const [stats, setStats] = useState(null);
  const [followupTypes, setFollowupTypes] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "calendar";
  const setActiveTab = (val) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", val);
    setSearchParams(nextParams);
  };

  const handleMonthChange = (newMonth) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("month", format(newMonth, "yyyy-MM"));
    setSearchParams(nextParams);
  };

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedFollowup, setSelectedFollowup] = useState(null);

  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [editFollowup, setEditFollowup] = useState(null);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteFollowup, setDeleteFollowup] = useState(null);

  const [calLoading, setCalLoading] = useState(true);
  const [calendarData, setCalendarData] = useState({});
  const monthStr = useMemo(() => {
    const mParam = searchParams.get("month");
    if (mParam && parseMonthParam(mParam)) {
      return mParam;
    }
    const dParam = searchParams.get("date");
    const parsedD = parseDateParam(dParam);
    if (parsedD) {
      return format(parsedD, "yyyy-MM");
    }
    return format(new Date(), "yyyy-MM");
  }, [searchParams]);

  const currentMonth = useMemo(() => {
    return parseMonthParam(monthStr);
  }, [monthStr]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayPanel, setShowDayPanel] = useState(false);

  const [listLoading, setListLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(20);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedCalRowId, setExpandedCalRowId] = useState(null);
  const toggleRow = (id) => setExpandedRowId(prev => prev === id ? null : id);
  const toggleCalRow = (id) => setExpandedCalRowId(prev => prev === id ? null : id);

  useEffect(() => {
    if (loading) return;
    const fetchTypes = async () => {
      try {
        const res = await api.get("/followups/types");
        setFollowupTypes(res.data.types || []);
      } catch (err) { console.error(err); }
    };
    fetchTypes();
  }, [loading]);

  useEffect(() => {
    if (loading || currentBranch === undefined) return;
    const fetchStaff = async () => {
      try {
        const res = await api.get(`/followups/staff/list${currentBranch ? `?branchId=${currentBranch.id}` : ""}`);
        setStaffList(res.data.staff || []);
      } catch (err) { console.error(err); }
    };
    fetchStaff();
  }, [currentBranch, loading]);

  const monthStats = useMemo(() => {
    const stats = { od: 0, up: 0, cd: 0 };
    Object.values(calendarData).forEach(dayData => {
      if (dayData.followups) {
        dayData.followups.forEach(f => {
          const ts = getTemporalStatus(f, orgTimezone);
          if (ts === "Overdue") stats.od++;
          else if (ts === "Upcoming") stats.up++;
          else if (ts === "Closed") stats.cd++;
        });
      }
    });
    return stats;
  }, [calendarData]);

  const fetchStats = useCallback(async () => {
    try {
      const params = buildFilterParams(filters, currentBranch);
      const today = format(new Date(), "yyyy-MM-dd");
      params.append("today", today);
      const res = await api.get(`/followups/stats?${params.toString()}`);
      setStats(res.data);
    } catch { }
  }, [filters, currentBranch]);

  useEffect(() => {
    if (loading || currentBranch === undefined) return;
    fetchStats();
  }, [fetchStats, currentBranch, loading]);

  const fetchCalendarData = useCallback(async () => {
    setCalLoading(true);
    try {
      const month = format(currentMonth, "yyyy-MM");
      const params = buildFilterParams(filters, currentBranch);
      params.append("month", month);
      const res = await api.get(`/followups/calendar?${params.toString()}`);
      setCalendarData(res.data.calendar || {});
    } catch (err) { console.error(err); }
    finally { setCalLoading(false); }
  }, [currentMonth, currentBranch, filters]);

  useEffect(() => {
    if (loading || currentBranch === undefined) return;
    fetchCalendarData();
  }, [fetchCalendarData, currentBranch, loading]);

  useEffect(() => {
    const dParam = searchParams.get("date");
    const parsedD = parseDateParam(dParam);
    if (parsedD) {
      const dateStr = format(parsedD, "yyyy-MM-dd");
      const dayData = calendarData[dateStr] || { total: 0, high: 0, medium: 0, low: 0, followups: [] };
      setSelectedDay({ date: parsedD, data: dayData });
    } else if (calendarData && Object.keys(calendarData).length > 0) {
      if (selectedDay) {
        const selStr = format(selectedDay.date, "yyyy-MM-dd");
        const updatedData = calendarData[selStr] || { total: 0, high: 0, medium: 0, low: 0, followups: [] };
        setSelectedDay({ date: selectedDay.date, data: updatedData });
      } else {
        const today = new Date();
        const dateStr = format(today, "yyyy-MM-dd");
        const dayData = calendarData[dateStr] || { total: 0, high: 0, medium: 0, low: 0, followups: [] };
        setSelectedDay({ date: today, data: dayData });
      }
    } else if (!selectedDay) {
      const today = new Date();
      setSelectedDay({ date: today, data: { total: 0, high: 0, medium: 0, low: 0, followups: [] } });
    }
  }, [calendarData, searchParams]);

  const fetchListData = useCallback(async () => {
    setListLoading(true);
    try {
      const params = buildFilterParams(filters, currentBranch);
      params.append("page", listPage);
      params.append("pageSize", listPageSize);
      const res = await api.get(`/followups?${params.toString()}`);
      setListData(res.data.followups || []);
      setListTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setListLoading(false); }
  }, [filters, currentBranch, listPage, listPageSize]);

  useEffect(() => { setListPage(1); }, [filters, listPageSize, currentBranch]);
  useEffect(() => {
    if (loading || currentBranch === undefined) return;
    if (activeTab === "list") {
      fetchListData();
    }
  }, [fetchListData, activeTab, currentBranch, loading]);

  const refreshAll = useCallback(() => {
    fetchCalendarData();
    fetchStats();
    if (activeTab === "list") {
      fetchListData();
    }
  }, [fetchCalendarData, fetchStats, fetchListData, activeTab]);

  const handleViewFollowup = async (followup) => {
    try {
      const res = await api.get(`/followups/${followup.id}`);
      setSelectedFollowup({ ...res.data.followup, activities: res.data.activities });
      setShowDetailsModal(true);
    } catch { toast.error("Failed to load followup details"); }
  };

  const handleEditFollowup = async (followup) => {
    try {
      const res = await api.get(`/followups/${followup.id}`);
      setEditFollowup(res.data.followup);
      setShowEditDrawer(true);
    } catch { toast.error("Failed to load followup"); }
  };

  const handleDeleteFollowup = (followup) => {
    setDeleteFollowup(followup);
    setShowDeleteDialog(true);
  };

  const handleDayClick = (day) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("date", format(day, "yyyy-MM-dd"));
    nextParams.set("month", format(day, "yyyy-MM"));
    setSearchParams(nextParams);
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const filterCount = activeFilterCount(filters);
  const totalPages = Math.max(1, Math.ceil(listTotal / listPageSize));

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-1.5 mt-1 md:space-y-3" data-testid="followups-page">

      {/* ── Header ── */}
      <div className="flex flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-lg md:text-3xl font-black font-heading tracking-tight uppercase">Member Follow-ups</h1>
          <p className="text-xs text-muted-foreground tracking-tight">Manage and track member follow-ups</p>
        </div>
        <Button
          variant={filterCount > 0 ? "secondary" : "outline"}
          size="sm"
          onClick={() => setShowFiltersDrawer(true)}
          className="font-heading uppercase tracking-wide text-xs self-start sm:self-auto"
          data-testid="toggle-filters-btn"
        >
          <Filter className="h-4 w-4 mr-2" />Filters
          {filterCount > 0 && (
            <Badge className="ml-1.5 bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">{filterCount}</Badge>
          )}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex overflow-x-auto gap-1 pb-1 -mx-4 px-4 md:grid md:grid-cols-5 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { label: "Total", value: stats.total, color: "" },
            { label: "Today", value: stats.todayCount, color: "text-blue-500" },
            { label: "Overdue", value: stats.overdueCount, color: "text-red-500" },
            { label: "Need F/U", value: stats.needFollowup, color: "text-purple-500" },
            { label: "High Pri", value: stats.highPriority, color: "text-red-500" },
          ].map(s => (
            <Card key={s.label} className="bg-card border-border">
              <CardContent className="px-2 py-1  text-center">
                <p className={`text-xl font-black font-heading ${s.color}`}>{s.value}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 space-y-2 md:space-y-3">
        <TabsList className="bg-secondary/50 p-1 justify-start">
          <TabsTrigger value="calendar" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-calendar">
            <CalendarIcon className="h-3.5 w-3.5 mr-2" />Calendar
          </TabsTrigger>
          <TabsTrigger value="list" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-list">
            <List className="h-3.5 w-3.5 mr-2" />List
          </TabsTrigger>
        </TabsList>

        {/* ══ CALENDAR TAB ════════════════════════════════════════════════════════════════════════════ */}
        <TabsContent value="calendar">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">
            {/* Left: Calendar Card */}
            <Card className="lg:col-span-5 bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 py-2">
                <div className="flex flex-col gap-1">
                  <CardTitle className="text-sm font-heading uppercase tracking-wide leading-none">Calendar</CardTitle>
                  <div className="flex items-center gap-2 text-[10px] font-mono font-bold leading-none">
                    {monthStats.od > 0 && <span className="text-red-500">OD: {monthStats.od}</span>}
                    {monthStats.up > 0 && <span className="text-cyan-500">UP: {monthStats.up}</span>}
                    {monthStats.cd > 0 && <span className="text-zinc-500">CD: {monthStats.cd}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleMonthChange(subMonths(currentMonth, 1))} data-testid="prev-month-btn"><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm font-medium min-w-[100px] text-center">{format(currentMonth, "MMMM yyyy")}</span>
                  <Button variant="ghost" size="icon" onClick={() => handleMonthChange(addMonths(currentMonth, 1))} data-testid="next-month-btn"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="p-1">
                {calLoading ? (
                  <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>
                ) : (
                  <>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {DAYS_OF_WEEK.map(d => (
                        <div key={d.key} className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground py-2">{d.label}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) }).map((day, idx) => {
                        const dayOfWeek = day.getDay();
                        const isFirstDay = idx === 0;
                        const dateStr = format(day, "yyyy-MM-dd");
                        const dayData = calendarData[dateStr];
                        const hasFollowups = dayData?.total > 0;
                        const today = isToday(day);
                        const isSelected = selectedDay && format(selectedDay.date, "yyyy-MM-dd") === dateStr;

                        const counts = hasFollowups ? dayData.followups?.reduce((acc, f) => {
                          const ts = getTemporalStatus(f, orgTimezone);
                          if (ts === "Overdue") acc.od++;
                          else if (ts === "Upcoming") acc.up++;
                          else if (ts === "Closed") acc.cd++;
                          return acc;
                        }, { od: 0, up: 0, cd: 0 }) : null;

                        return (
                          <div key={day.toISOString()}
                            style={{ gridColumn: isFirstDay ? dayOfWeek + 1 : undefined }}
                            onClick={() => handleDayClick(day)}
                            className={`min-h-[60px] sm:min-h-[80px] p-1 sm:p-2 rounded-sm border cursor-pointer transition-all ${today ? "border-primary bg-primary/5 shadow-[0_0_10px_rgba(var(--primary),0.1)]"
                              : isSelected ? "border-primary bg-primary/5"
                                : hasFollowups ? "border-border bg-secondary/30 hover:bg-secondary/50"
                                  : "border-border/50 bg-background hover:bg-secondary/20"
                              }`} data-testid={`calendar-day-${dateStr}`}
                          >
                            <div className={`text-[10px] font-medium mb-1 flex justify-between ${today ? "text-primary" : ""}`}><span>{format(day, "d")}</span>{counts && (
                              <span className={`text-[9px] font-medium w-4 h-4 flex items-center justify-center rounded-full ${counts.od > 0 ? "bg-red-500 text-white" : ""} ${counts.up > 0 ? "bg-blue-500 text-white" : ""} ${counts.cd > 0 ? "bg-green-500 text-white" : ""}`}>{dayData.total}</span>
                            )}</div>
                            {hasFollowups && (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                </div>
                                <div className="flex gap-1 flex-wrap">
                                  {dayData.high > 0 && <div className="h-1.5 w-1.5 rounded-full bg-red-500" title={`${dayData.high} High`} />}
                                  {dayData.medium > 0 && <div className="h-1.5 w-1.5 rounded-full bg-amber-500" title={`${dayData.medium} Medium`} />}
                                  {dayData.low > 0 && <div className="h-1.5 w-1.5 rounded-full bg-blue-500" title={`${dayData.low} Low`} />}
                                </div>
                                {counts && (
                                  <div className="flex flex-wrap gap-x-1.5 gap-y-0 text-[9px] font-mono font-bold leading-none mt-1">
                                    {counts.od > 0 && <span className="text-red-500">OD:{counts.od}</span>}
                                    {counts.up > 0 && <span className="text-cyan-500">UP:{counts.up}</span>}
                                    {counts.cd > 0 && <span className="text-zinc-500">CD:{counts.cd}</span>}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6 mt-4 pt-4 border-t border-border flex-wrap">
                      {[
                        { color: "bg-red-500", label: "High" },
                        { color: "bg-amber-500", label: "Medium" },
                        { color: "bg-blue-500", label: "Low" },
                        { color: "text-red-500", label: "OD", desc: "Overdue", type: "text" },
                        { color: "text-cyan-500", label: "UP", desc: "Upcoming", type: "text" },
                        { color: "text-zinc-500", label: "CD", desc: "Closed", type: "text" },
                      ].map(l => (
                        <div key={l.label} className="flex items-center gap-1.5">
                          {l.type === "text" ? (
                            <span className={`text-[9px] font-mono font-bold ${l.color}`}>{l.label}</span>
                          ) : (
                            <div className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                          )}
                          <span className="text-[11px] text-muted-foreground">{l.desc || l.label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Right: Day Details Card */}
            <Card className="lg:col-span-7 bg-card border-border flex flex-col h-full min-h-[500px] lg:h-[calc(100vh-280px)]">
              <CardHeader className="px-3 py-2 border-b border-border shrink-0">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-primary" />
                  <CardTitle className="text-sm font-heading uppercase tracking-wide">
                    {selectedDay ? format(selectedDay.date, "EEEE, MMM d, yyyy") : "Daily Details"}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-0 scroll-smooth">
                {!selectedDay ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
                      <CalendarIcon className="h-6 w-6" />
                    </div>
                    <p className="text-sm font-medium">Select a day from the calendar</p>
                    <p className="text-xs mt-1">to view scheduled follow-ups</p>
                  </div>
                ) : selectedDay.data.total === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                    <MessageSquare className="h-10 w-10 mb-3" />
                    <p className="text-sm">No follow-ups scheduled for this day</p>
                  </div>
                ) : (
                  <div className="p-1 md:p-4 space-y-2">
                    {/* Day Summary */}
                    <div className="flex items-center justify-between px-3 py-2 bg-secondary/30 rounded-xl border border-border">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-baseline gap-2">
                          <p className="text-lg font-black font-heading leading-none">{selectedDay.data.total}</p>
                          <p className="text-[10px] text-muted-foreground font-mono uppercase">Total Tasks</p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            const counts = selectedDay.data.followups.reduce((acc, f) => {
                              const ts = getTemporalStatus(f, orgTimezone);
                              acc[ts] = (acc[ts] || 0) + 1;
                              return acc;
                            }, {});
                            return (
                              <>
                                {counts.Overdue > 0 && <Badge className={`${TEMPORAL_STATUS_COLORS.Overdue} border text-[9px] h-4 px-1.5`}>{counts.Overdue} Overdue</Badge>}
                                {counts.Upcoming > 0 && <Badge className={`${TEMPORAL_STATUS_COLORS.Upcoming} border text-[9px] h-4 px-1.5`}>{counts.Upcoming} Upcoming</Badge>}
                                {counts.Closed > 0 && <Badge className={`${TEMPORAL_STATUS_COLORS.Closed} border text-[9px] h-4 px-1.5`}>{counts.Closed} Closed</Badge>}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <p className="text-[10px] text-muted-foreground font-mono uppercase">Priority</p>
                        <div className="flex gap-1">
                          {selectedDay.data.high > 0 && <Badge className={`${PRIORITY_COLORS.High}   border text-[9px] h-4 px-1`}>H:{selectedDay.data.high}</Badge>}
                          {selectedDay.data.medium > 0 && <Badge className={`${PRIORITY_COLORS.Medium} border text-[9px] h-4 px-1`}>M:{selectedDay.data.medium}</Badge>}
                          {selectedDay.data.low > 0 && <Badge className={`${PRIORITY_COLORS.Low}    border text-[9px] h-4 px-1`}>L:{selectedDay.data.low}</Badge>}
                        </div>
                      </div>
                    </div>

                    {/* Follow-ups List */}
                    <div className="space-y-2">
                      {selectedDay.data.followups.map(followup => {
                        const isExpanded = expandedCalRowId === followup.id;
                        return (
                          <div
                            key={followup.id}
                            className="group bg-card border border-border rounded-xl hover:border-primary/50 transition-colors shadow-sm overflow-hidden"
                            data-testid={`day-followup-${followup.id}`}
                          >
                            {/* Header / Clickable area to toggle */}
                            <div
                              onClick={() => toggleCalRow(followup.id)}
                              className="px-3 py-2 cursor-pointer flex items-center justify-between gap-3 select-none"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <ClickableAvatar
                                  src={followup.memberPhoto}
                                  name={followup.memberName}
                                  className="h-8 w-8 shrink-0 ring-1 ring-border group-hover:ring-primary/30 transition-all"
                                  fallbackClassName="bg-primary/10 text-primary text-xs font-semibold"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-baseline gap-1.5 flex-wrap">
                                    <p className="font-semibold text-sm truncate">{followup.memberName}</p>
                                    <span
                                      className="text-[10px] text-muted-foreground flex items-center gap-0.5 font-mono"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Phone className="h-2.5 w-2.5" />{followup.memberPhone}
                                    </span>
                                  </div>
                                  <p className="text-xs font-medium text-muted-foreground truncate">{followup.title}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2.5 shrink-0">
                                <div className="flex items-end flex-col gap-1">
                                  {followup.scheduledTime && (
                                    <div className="text-[10px] tracking-tight text-muted-foreground flex items-center gap-1 font-mono">
                                      <Clock className="h-2.5 w-2.5" /> <span>{formatTimeAMPM(followup.scheduledTime)}</span>
                                    </div>
                                  )}
                                  <div className="flex gap-1">
                                    {
                                      followup.status !== "Closed" && (
                                        <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(followup, orgTimezone)]} border text-[8px] font-mono uppercase h-3.5 px-1`}>{getTemporalStatus(followup, orgTimezone)}</Badge>
                                      )

                                    }
                                    <Badge className={`${PRIORITY_COLORS[followup.priority] || PRIORITY_COLORS.Medium} border text-[8px] font-mono uppercase h-3.5 px-1`}>
                                      {followup.priority}
                                    </Badge>
                                    <Badge className={`${STATUS_COLORS[followup.status] || STATUS_COLORS.Open} border text-[8px] font-mono uppercase h-3.5 px-1`}>
                                      {followup.status}
                                    </Badge>

                                  </div>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0 ${isExpanded ? "rotate-180" : ""}`} />
                              </div>
                            </div>

                            {/* Accordion Content */}
                            {isExpanded && (
                              <div className="border-t border-border bg-secondary/15 text-xs space-y-2.5 ">
                                {/* Action Buttons */}
                                {followup.memberPhone && (
                                  <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
                                    <a
                                      href={`tel:${followup.memberPhone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
                                    >
                                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
                                        <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                                      </div>
                                      <div className="flex flex-col items-start leading-tight min-w-0">
                                        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                                          Mobile Call
                                        </span>
                                        <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                                          Call Now
                                        </span>
                                      </div>
                                    </a>
                                    <a
                                      href={`https://wa.me/${followup.memberPhone}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
                                    >
                                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
                                        <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                      </div>
                                      <div className="flex flex-col items-start leading-tight min-w-0">
                                        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                                          Message
                                        </span>
                                        <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                                          WhatsApp
                                        </span>
                                      </div>
                                    </a>
                                  </div>
                                )}
                                <div className="flex items-center justify-end gap-2  border-b border-border/50 bg-secondary/20  p-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-[10px] gap-1 font-heading uppercase tracking-wider"
                                    onClick={(e) => { e.stopPropagation(); handleViewFollowup(followup); }}
                                  >
                                    <Eye className="h-3 w-3" /> View Details & Notes
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-[10px] gap-1 font-heading uppercase tracking-wider"
                                    onClick={(e) => { e.stopPropagation(); handleEditFollowup(followup); }}
                                  >
                                    <Edit2 className="h-3 w-3" /> Edit
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-[10px] gap-1 font-heading uppercase tracking-wider text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteFollowup(followup); }}
                                  >
                                    <Trash2 className="h-3 w-3" /> Delete
                                  </Button>
                                </div>

                                {/* Description */}
                                <div className="space-y-1 px-3">
                                  <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Description</p>
                                  <p className="text-muted-foreground leading-normal">{followup.description || "No description provided."}</p>
                                </div>

                                {/* Types */}
                                <div className="space-y-1 px-3 ">
                                  <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Follow-up Types</p>
                                  <div className="flex flex-wrap gap-1">
                                    {followup.followupTypeNames?.length > 0 ? (
                                      followup.followupTypeNames.map((name, i) => (
                                        <Badge key={i} variant="outline" className="text-[9px] font-mono py-0 h-4">
                                          {name}
                                        </Badge>
                                      ))
                                    ) : (
                                      <span className="text-muted-foreground">—</span>
                                    )}
                                  </div>
                                </div>

                                {/* Assigned Staff & Branch */}

                                {followup.assignedStaffName && (
                                  <div className="grid grid-cols-2 gap-2 pt-1.5 border-t border-border/50 px-3">
                                    <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Assigned Staff</p>
                                    <p className="font-medium text-foreground mt-0.5">{followup.assignedStaffName || "Unassigned"}</p>
                                  </div>
                                )}

                                {/* Notes / Comments */}
                                <div className="px-2 pb-2">
                                  <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Notes / Comments</p>
                                  {followup.notes && Array.isArray(followup.notes) && followup.notes.length > 0 ? (
                                    <div className="max-h-48 overflow-y-auto pr-1 space-y-1 [scrollbar-width:thin]">
                                      {followup.notes.map((note, idx) => {
                                        const noteAction = note.details?.includes("Status changed:") ? "status_changed" : "note_added";
                                        return (
                                          <div key={idx} className="bg-background/40 p-1.5 border border-border/30 rounded-lg">
                                            <div className="flex flex-wrap items-center gap-x-1.5 text-[9px] text-muted-foreground leading-none mb-1">
                                              <span className="font-bold text-foreground">
                                                {getActionLabel(noteAction)}
                                              </span>
                                              <span>•</span>
                                              <span className="font-medium">by {note.userName}</span>
                                              <span>•</span>
                                              <span className="font-mono text-[8.5px]">{note.createdAt ? formatDateTime(note.createdAt, orgTimezone) : ""}</span>
                                            </div>
                                            {renderNoteOrActivityDetails(note)}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : typeof followup.notes === "string" && followup.notes ? (
                                    <div className="bg-background/40 p-1.5 border border-border/30 rounded-lg">
                                      {renderNoteOrActivityDetails({ details: followup.notes })}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-muted-foreground italic pl-1">No notes added.</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ══ LIST TAB ════════════════════════════════════════════════════════════════════════════ */}
        <TabsContent value="list" className="flex-1 min-h-0 flex flex-col">
          <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0 md:mx-0 rounded-lg">

            {listLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              </div>
            ) : listData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <MessageSquare className="h-10 w-10 mb-3" />
                <p className="text-sm">No follow-ups found</p>
                {filterCount > 0 && (
                  <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={clearFilters}>
                    <X className="h-3.5 w-3.5 mr-1" />Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* ── Mobile card list (hidden on md+) ── */}
                <div className="md:hidden overflow-y-auto flex-1">
                  {listData.map(f => (
                    <FollowupMobileCard
                      key={f.id}
                      f={f}
                      expanded={expandedRowId === f.id}
                      onToggle={() => toggleRow(f.id)}
                      onView={handleViewFollowup}
                      onEdit={handleEditFollowup}
                      onDelete={handleDeleteFollowup}
                      tz={orgTimezone}
                    />
                  ))}
                </div>

                {/* ── Desktop table (hidden below md) ── */}
                <div className="hidden md:block overflow-auto flex-1">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card z-10">
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Member</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Scheduled</TableHead>
                        <TableHead>Types</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assigned To</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="[&_tr:last-child]:border-1">
                      {listData.map(f => (
                        <Fragment key={f.id}>
                          <TableRow
                            className="group cursor-pointer hover:bg-secondary/50"
                            data-testid={`followup-row-${f.id}`}
                            onClick={() => toggleRow(f.id)}
                          >
                            <TableCell className="w-10">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); toggleRow(f.id); }}>
                                <ChevronDown className={`h-4 w-4 transition-transform ${expandedRowId === f.id ? "rotate-180" : ""}`} />
                              </Button>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-9 w-9 border border-border">
                                  {f.memberPhoto ? <AvatarImage src={`${BACKEND_URL}${f.memberPhoto}`} /> : null}
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading">{f.memberName?.[0]?.toUpperCase()}</AvatarFallback>
                                </Avatar>
                                <div className="cursor-pointer hover:text-primary transition-colors">
                                  <p className="font-medium text-sm leading-tight">{f.memberName}</p>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    {f.memberNumber && <span className="font-mono uppercase">#{f.memberNumber}</span>}
                                    {f.memberNumber && f.memberPhone && <span>•</span>}
                                    {f.memberPhone || "—"}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm font-medium">{f.title}</p>
                              {f.description && <p className="text-xs text-muted-foreground truncate max-w-[160px]">{f.description}</p>}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                <span className="font-mono text-sm flex items-center gap-1"><CalendarIcon className="h-3 w-3 text-muted-foreground" />{f.scheduledDate}</span>
                                {f.scheduledTime && <span className="font-mono text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{formatTimeAMPM(f.scheduledTime)}</span>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {f.followupTypeNames?.length > 0
                                  ? f.followupTypeNames.map((name, i) => <Badge key={i} variant="outline" className="text-[10px] font-mono">{name}</Badge>)
                                  : <span className="text-muted-foreground text-xs">—</span>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {
                                  f.status !== "Closed" && (
                                    <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(f, orgTimezone)]} border text-[10px] font-mono uppercase w-fit`}>{getTemporalStatus(f, orgTimezone)}</Badge>
                                  )

                                }
                                <Badge className={`${STATUS_COLORS[f.status] || STATUS_COLORS.Open} border text-[10px] font-mono uppercase w-fit`}>{f.status}</Badge>
                                <Badge className={`${PRIORITY_COLORS[f.priority] || PRIORITY_COLORS.Medium} border text-[10px] font-mono uppercase w-fit`}>{f.priority}</Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{f.assignedStaffName || <span className="text-muted-foreground">—</span>}</span>
                            </TableCell>
                          </TableRow>
                          {expandedRowId === f.id && (
                            <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border">
                              <TableCell colSpan={8} className="p-0">
                                <div className="flex flex-col animate-in fade-in duration-200">
                                  {f.memberPhone && (
                                    <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
                                      <a
                                        href={`tel:${f.memberPhone}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
                                      >
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
                                          <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                                        </div>
                                        <div className="flex flex-col items-start leading-tight min-w-0">
                                          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                                            Mobile Call
                                          </span>
                                          <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                                            Call Now
                                          </span>
                                        </div>
                                      </a>
                                      <a
                                        href={`https://wa.me/${f.memberPhone}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
                                      >
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
                                          <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        <div className="flex flex-col items-start leading-tight min-w-0">
                                          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                                            Message
                                          </span>
                                          <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                                            WhatsApp
                                          </span>
                                        </div>
                                      </a>
                                    </div>
                                  )}
                                  {/* Action Bar */}
                                  <div className="flex items-center justify-between gap-2 p-2 border-b border-border bg-secondary/20">
                                    <div className="text-xs text-muted-foreground font-mono hidden md:block">
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                                        onClick={(e) => { e.stopPropagation(); handleViewFollowup(f); }}>
                                        <Eye className="h-3.5 w-3.5" /> <span className="hidden md:block">View Details & Notes</span>
                                      </Button>
                                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                                        onClick={(e) => { e.stopPropagation(); handleEditFollowup(f); }}>
                                        <Edit2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Edit Follow-up</span>
                                      </Button>
                                      <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteFollowup(f); }}>
                                        <Trash2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Delete Follow-up</span>
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 text-sm">
                                    <div className="space-y-1.5">
                                      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-1">Description</p>
                                      <p className="text-muted-foreground text-xs leading-normal">{f.description || "No description provided."}</p>
                                    </div>
                                    <div className="space-y-1.5">
                                      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-1">Follow-up Types</p>
                                      <div className="flex flex-wrap gap-1">
                                        {f.followupTypeNames?.length > 0 ? (
                                          f.followupTypeNames.map((name, i) => (
                                            <Badge key={i} variant="outline" className="text-[10px] font-mono">
                                              {name}
                                            </Badge>
                                          ))
                                        ) : (
                                          <span className="text-muted-foreground text-xs">—</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="space-y-1.5">
                                      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-1.5">Notes / Comments</p>
                                      {f.notes && Array.isArray(f.notes) && f.notes.length > 0 ? (
                                        <div className="max-h-48 overflow-y-auto pr-1 space-y-1.5 [scrollbar-width:thin]">
                                          {f.notes.map((note, idx) => (
                                            <div key={idx} className="bg-background/60 p-1.5 rounded-lg border border-border/50">
                                              <p className="text-xs leading-normal text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{note.details}</p>
                                              <p className="text-[9px] text-slate-400 dark:text-slate-500 mt-0.5">
                                                {note.userName} · {note.createdAt ? formatDateTime(note.createdAt, orgTimezone) : ""}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      ) : typeof f.notes === "string" && f.notes ? (
                                        <p className="text-muted-foreground text-xs leading-normal whitespace-pre-wrap">{f.notes}</p>
                                      ) : (
                                        <p className="text-muted-foreground text-xs italic">No notes added.</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* ── Pagination ── */}
                <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="font-mono hidden sm:block">{listTotal} total</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs">Show:</span>
                      <Select value={String(listPageSize)} onValueChange={(v) => { setListPageSize(Number(v)); setListPage(1); }}>
                        <SelectTrigger className="w-20 h-8" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={listPage === 1} onClick={() => setListPage(listPage - 1)} data-testid="page-prev-btn">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {listPage} of {totalPages || 1}</span>
                    <Button variant="outline" size="sm" disabled={listPage * listPageSize >= listTotal} onClick={() => setListPage(listPage + 1)} data-testid="page-next-btn">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Filters Drawer ── */}
      <FiltersDrawer
        open={showFiltersDrawer} onOpenChange={setShowFiltersDrawer}
        filters={filters} onChange={setFilters} onClear={clearFilters}
        followupTypes={followupTypes} staffList={staffList}
      />

      {/* ── Edit Drawer ── */}
      <EditFollowupDrawer
        open={showEditDrawer} onOpenChange={setShowEditDrawer}
        followup={editFollowup}
        followupTypes={followupTypes} staffList={staffList}
        onSuccess={refreshAll}
      />

      {/* ── Delete Dialog ── */}
      <DeleteFollowupDialog
        open={showDeleteDialog} onOpenChange={setShowDeleteDialog}
        followup={deleteFollowup}
        onSuccess={refreshAll}
      />



      {/* ── Followup Details Modal ── */}
      <FollowupDetailsModal
        open={showDetailsModal} onOpenChange={setShowDetailsModal}
        followup={selectedFollowup}
        orgTimezone={orgTimezone}
        onSuccess={() => {
          fetchCalendarData(); fetchStats();
          if (activeTab === "list") fetchListData();
          if (selectedFollowup) handleViewFollowup(selectedFollowup);
        }}
      />
    </div>
  );
}

// ── Followup Details Modal ──────────────────────────────────────────────────────────────────────────────────────────────
function FollowupDetailsModal({ open, onOpenChange, followup, orgTimezone, onSuccess }) {
  const [newNote, setNewNote] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [nextTime, setNextTime] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && followup) {
      setNewStatus("");
      setNewNote("");
      setNextDate("");
      setNextTime("");
    }
  }, [open, followup]);

  if (!followup) return null;

  const handleUpdateFollowup = async () => {
    if (!newStatus) {
      toast.error("Please select a status");
      return;
    }
    if (newStatus === "Need Followup" && (!nextDate || !nextTime)) {
      toast.error("Next schedule date and time is required");
      return;
    }

    const isStatusUpdate = newStatus !== followup.status || (newStatus === "Need Followup" && (nextDate || nextTime));
    const hasNote = !!newNote.trim();

    if (!isStatusUpdate && !hasNote) {
      toast.error("Please select a different status or enter a response note");
      return;
    }

    setLoading(true);
    try {
      if (isStatusUpdate) {
        await api.post(`/followups/${followup.id}/status`, {
          status: newStatus,
          notes: newNote.trim(),
          nextScheduledDate: nextDate,
          nextScheduledTime: nextTime,
        });
        toast.success("Followup updated");
      } else {
        await api.post(`/followups/${followup.id}/notes`, {
          notes: newNote.trim(),
        });
        toast.success("Note added");
      }
      setNewNote("");
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update followup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="font-heading uppercase tracking-wide">Followup Details</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-secondary/30 rounded-xl">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                {followup.memberPhoto ? <AvatarImage src={`${BACKEND_URL}${followup.memberPhoto}`} /> : null}
                <AvatarFallback className="bg-primary text-primary-foreground">{followup.memberName?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-heading font-semibold">{followup.memberName}</p>
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Phone className="h-3.5 w-3.5" />{followup.memberPhone}
                </span>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.open(`/members/${followup.memberId}`, "_blank")} className="text-xs w-full sm:w-auto">View Member</Button>
          </div>
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="text-lg font-heading font-semibold">{followup.title}</h3>
              <div className="flex items-center gap-2">
                <Badge className={`${PRIORITY_COLORS[followup.priority]} border text-[10px] font-mono uppercase`}>{followup.priority}</Badge>
                <Badge className={`${STATUS_COLORS[followup.status]}   border text-[10px] font-mono uppercase`}>{followup.status}</Badge>
              </div>
            </div>
            {followup.description && <p className="text-sm text-muted-foreground">{followup.description}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Scheduled</p>
                <p className="font-mono">{followup.scheduledDate}{followup.scheduledTime && ` at ${formatTimeAMPM(followup.scheduledTime)}`}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Assigned To</p>
                <p>{followup.assignedStaffName || "Not assigned"}</p>
              </div>
            </div>
            {followup.followupTypeNames?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Follow-up Types</p>
                <div className="flex flex-wrap gap-1">
                  {followup.followupTypeNames.map((name, i) => <Badge key={i} variant="outline" className="text-xs">{name}</Badge>)}
                </div>
              </div>
            )}
          </div>

          {/* Unified Add Response & Status Change */}
          <div className="space-y-4 p-4 border border-border rounded-xl bg-card">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="modal-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {newStatus === "Need Followup" && (
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-purple-500">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-xs font-medium">Reschedule Required</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Next Date *</Label>
                    <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} data-testid="modal-next-date-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Next Time *</Label>
                    <Input type="time" value={nextTime} onChange={e => setNextTime(e.target.value)} data-testid="modal-next-time-input" />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Response / Notes</Label>
              <Textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Enter followup response or notes here..."
                rows={3}
                data-testid="modal-note-input"
              />
            </div>

            <Button
              size="sm"
              onClick={handleUpdateFollowup}
              disabled={loading}
              data-testid="modal-confirm-status-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />Save Response
            </Button>
          </div>

          <div className="space-y-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Activity History</p>
            {followup.activities?.length > 0 ? (
              <div className="space-y-2">
                {followup.activities.map(activity => (
                  <div key={activity.id} className="flex gap-2.5 p-2.5 bg-secondary/20 dark:bg-secondary/10 border border-border/40 rounded-lg">
                    <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${
                      activity.action === "created" ? "bg-blue-500/10 text-blue-500" :
                      activity.action === "updated" ? "bg-indigo-500/10 text-indigo-500" :
                      activity.action === "status_changed" ? "bg-emerald-500/10 text-emerald-500" :
                      activity.action === "note_added" ? "bg-amber-500/10 text-amber-500" :
                      "bg-secondary text-muted-foreground"
                    }`}>
                      {activity.action === "created" && <Plus className="h-3.5 w-3.5" />}
                      {activity.action === "updated" && <Edit2 className="h-3.5 w-3.5" />}
                      {activity.action === "status_changed" && <CheckCircle2 className="h-3.5 w-3.5" />}
                      {activity.action === "note_added" && <MessageSquare className="h-3.5 w-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-1.5 text-[10px] text-muted-foreground leading-none">
                        <span className="font-bold font-heading uppercase tracking-wide text-foreground text-xs">
                          {getActionLabel(activity.action)}
                        </span>
                        <span>•</span>
                        <span className="font-medium">by {activity.userName}</span>
                        <span>•</span>
                        <span className="font-mono text-[9px]">{formatDateTime(activity.createdAt, orgTimezone)}</span>
                      </div>
                      {renderNoteOrActivityDetails(activity)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No activity recorded</p>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
